import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { Refresh, FilterAltOff } from "@mui/icons-material";
import { useSelector } from "react-redux";
import AppState from "../../../../../../redux/appStore";
import ComponentLoader from "../../../../../../common/loader/componentLoader";

import { SearchForm } from "./components/SearchForm";
import { OrdersTable } from "./components/OrdersTable";
import { useStatusSelection } from "./hooks/useStatusSelection";
import { useOrdersSearch } from "./hooks/useOrdersSearch";
import Styles from "./queueOrdersReport.style";

interface QueueOrdersReportProps {
  readonly queueId: string | undefined;
}

export default function QueueOrdersReport({
  queueId,
}: QueueOrdersReportProps) {
  // Search form state
  const [orderNumbers, setOrderNumbers] = useState<string[]>([]);
  const [actionDate, setActionDate] = useState<string>("");
  const [showOnlyNullActionDate, setShowOnlyNullActionDate] = useState<boolean>(false);
  const [filterSearch, setFilterSearch] = useState<string>("");

  // Redux state
  const queuesInitialized = useSelector(
    (state: AppState) => state.queueState.initialized
  );

  // Custom hooks
  const {
    selectedStatuses,
    appliedStatuses,
    statusDropdownOpen,
    setStatusDropdownOpen,
    handleStatusChange,
    handleStatusClose,
    applyStatusFilter,
    resetStatuses,
  } = useStatusSelection({
    onStatusChange: () => {
      // Trigger search when status changes
    },
    onPageReset: () => {
      setPageNumber(1);
    },
  });

  const {
    orders,
    totalCount,
    pageNumber,
    pageSize,
    sortBy,
    sortDirection,
    isLoading,
    error,
    setPageNumber,
    handleSort,
    handlePageChange,
    handlePageSizeChange,
    searchOrders,
    resetSearch,
  } = useOrdersSearch({
    queueId,
    orderNumbers,
    selectedStatuses: appliedStatuses,
    actionDate,
    showOnlyNullActionDate,
    filterSearch,
  });



  // Clear functions for individual filters
  const clearOrderNumbers = () => {
    setOrderNumbers([]);
  };

  const clearActionDate = () => {
    setActionDate("");
    setShowOnlyNullActionDate(false);
  };

  const clearFilterSearch = () => {
    setFilterSearch("");
  };



  // Reset all filters when queue ID changes
  useEffect(() => {
    if (queueId) {
      // Reset all filter states to defaults
      setOrderNumbers([]);
      resetStatuses();
      setActionDate("");
      setShowOnlyNullActionDate(false);
      setFilterSearch("");
      setPageNumber(1);
    }
  }, [queueId, resetStatuses, setPageNumber]);

  const handleRefresh = () => {
    searchOrders();
  };

  const handleResetAllFilters = () => {
    // Reset all form states to defaults
    setOrderNumbers([]);
    resetStatuses();
    setActionDate("");
    setShowOnlyNullActionDate(false);
    setFilterSearch("");
    
    // Reset search/pagination state
    resetSearch();
  };

  // Don't render if queues are not initialized or queueId is missing
  if (!queuesInitialized || !queueId) {
    return null;
  }

  return (
    <Box>
      {/* Header with title and count badge */}
      <Box sx={Styles.remainingOrderCard}>
        <Typography
          data-auto-test-id="remaining-order-page-text"
          sx={Styles.remainingOrderTitle}
        >
          Total Orders
        </Typography>
        <Typography sx={Styles.remainingOrderBadge}>
          {totalCount?.toLocaleString("en-US") || 0}
        </Typography>
        <Box sx={Styles.headerButtonsContainer}>
          <IconButton
            onClick={handleResetAllFilters}
            sx={Styles.resetButton}
            size="small"
            title="Reset All Filters"
          >
            <FilterAltOff />
          </IconButton>
          <IconButton
            onClick={handleRefresh}
            sx={Styles.refreshButton}
            size="small"
            title="Refresh"
          >
            <Refresh />
          </IconButton>
        </Box>
      </Box>

      <ComponentLoader isOpen={isLoading}>
        {/* Search Form */}
        <SearchForm
          orderNumbers={orderNumbers}
          onOrderNumbersChange={setOrderNumbers}
          selectedStatuses={selectedStatuses}
          statusDropdownOpen={statusDropdownOpen}
          onStatusDropdownOpenChange={setStatusDropdownOpen}
          onStatusChange={handleStatusChange}
          onStatusClose={handleStatusClose}
          onApplyStatusFilter={applyStatusFilter}
          actionDate={actionDate}
          onActionDateChange={setActionDate}
          showOnlyNullActionDate={showOnlyNullActionDate}
          onShowOnlyNullActionDateChange={setShowOnlyNullActionDate}
          filterSearch={filterSearch}
          onFilterSearchChange={setFilterSearch}
          onClearOrderNumbers={clearOrderNumbers}
          onClearActionDate={clearActionDate}
          onClearFilterSearch={clearFilterSearch}
        />

        {/* Error Display */}
        {error && (
          <Box sx={Styles.errorContainer}>
            {error}
          </Box>
        )}

        {/* Data Table */}
        <OrdersTable
          orders={orders}
          totalCount={totalCount}
          pageNumber={pageNumber}
          pageSize={pageSize}
          sortBy={sortBy}
          sortDirection={sortDirection}
          isLoading={isLoading}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </ComponentLoader>
    </Box>
  );
}
